/**
 * Source Extraction Utility
 *
 * Groups citations by document and aggregates citation counts
 * following RAGFlow's approach.
 */ /**
 * Extract and group sources from parsed citations
 *
 * Groups citations by document name and counts how many times
 * each document is cited. Returns documents sorted by citation
 * count (most cited first).
 *
 * @param citations - Map of parsed citations
 * @returns Array of cited documents with counts and citation IDs
 */ export function extractSourcesFromCitations(citations) {
    // Group citations by document
    const docMap = new Map();
    citations.forEach((citation)=>{
        // Use documentId (UUID) as the key for grouping
        const key = citation.documentId || citation.documentName || 'Unknown';
        const docName = citation.documentName || 'Unknown Document';
        if (!docMap.has(key)) {
            // Create new document entry
            docMap.set(key, {
                name: docName,
                documentId: citation.documentId,
                type: citation.documentName?.split('.').pop()?.toUpperCase(),
                count: 0,
                citations: []
            });
        }
        // Update document with citation
        const doc = docMap.get(key);
        if (doc) {
            doc.count++;
            doc.citations.push(citation.id);
        }
    });
    // Sort by citation count (descending) then by name (ascending)
    return Array.from(docMap.values()).sort((a, b)=>{
        // Primary sort: most citations first
        if (b.count !== a.count) {
            return b.count - a.count;
        }
        // Secondary sort: alphabetically by name
        return a.name.localeCompare(b.name);
    });
}
/**
 * Get total citation count across all documents
 *
 * @param documents - Array of cited documents
 * @returns Total number of citations
 */ export function getTotalCitationCount(documents) {
    return documents.reduce((total, doc)=>total + doc.count, 0);
}
/**
 * Filter documents to only include those with specific citation IDs
 *
 * Useful for showing only documents that match certain criteria.
 *
 * @param documents - Array of cited documents
 * @param citationIds - Set of citation IDs to filter by
 * @returns Filtered array of documents
 */ export function filterDocumentsByCitationIds(documents, citationIds) {
    return documents.map((doc)=>({
            ...doc,
            citations: doc.citations.filter((id)=>citationIds.has(id)),
            count: doc.citations.filter((id)=>citationIds.has(id)).length
        })).filter((doc)=>doc.count > 0);
}
